import { View } from "@novorender/api";
import { ReadonlyQuat, ReadonlyVec3, vec2, vec3 } from "gl-matrix";

import { getCameraDir } from "features/engine2D/utils";
import { RenderState } from "features/render";
import { clampToSafeArea, type ViewportSafeArea } from "hooks/useViewportSafeArea";

type SectionPlane = RenderState["clipping"]["planes"][number];
type CanvasRect = Pick<DOMRect, "left" | "top" | "width" | "height">;

/** Returns screen-space move direction and distance-based multiplier for dragging a section plane. */
export function getScreenSpaceMoveDirAndMult(
    view: View,
    anchorPos: vec3,
    normal: vec3,
    outDir2d?: vec2,
    scratchVec3?: vec3,
): { dir2d: vec2; mult: number } | null {
    const anchorToCameraDist = vec3.dist(anchorPos, view.renderState.camera.position);
    const offset = Math.max(1, anchorToCameraDist / 10);
    const refPosWorld = scratchVec3 ?? vec3.create();
    vec3.scaleAndAdd(refPosWorld, anchorPos, normal, -offset);
    const screenPoints = view.convert.worldSpaceToScreenSpace([refPosWorld, anchorPos], { round: false });
    const refScreen = screenPoints[0];
    const anchorScreen = screenPoints[1];
    if (!refScreen || !anchorScreen) {
        return null;
    }
    const dir2d = outDir2d ?? vec2.create();
    vec2.sub(dir2d, refScreen, anchorScreen);
    const len = vec2.len(dir2d);
    if (len <= 1e-6) {
        return null;
    }
    const mult = offset / len;
    vec2.scale(dir2d, dir2d, 1 / len);
    return { dir2d, mult };
}

export function getGizmoScreenPos(view: View, anchor: ReadonlyVec3, canvasWidth: number, canvasHeight: number): vec2 {
    const [projected] = view.convert.worldSpaceToScreenSpace([anchor], { round: false });
    if (projected && Number.isFinite(projected[0]) && Number.isFinite(projected[1])) {
        return vec2.fromValues(projected[0], projected[1]);
    }

    // Behind the camera
    const { position, rotation } = view.renderState.camera;
    const right = vec3.transformQuat(vec3.create(), vec3.fromValues(1, 0, 0), rotation as ReadonlyQuat);
    const up = vec3.transformQuat(vec3.create(), vec3.fromValues(0, 1, 0), rotation as ReadonlyQuat);
    const rel = vec3.sub(vec3.create(), anchor, position);
    let dirX = vec3.dot(rel, right);
    let dirY = -vec3.dot(rel, up);
    const len = Math.hypot(dirX, dirY);
    if (len < 1e-6) {
        dirX = 1;
        dirY = 0;
    } else {
        dirX /= len;
        dirY /= len;
    }
    return vec2.fromValues(canvasWidth / 2 + dirX * canvasWidth * 2, canvasHeight / 2 + dirY * canvasHeight * 2);
}

export function getSectionPlanePoints(
    view: View,
    planes: RenderState["clipping"]["planes"],
    canvasWidth?: number,
    canvasHeight?: number,
) {
    const sectionPlanes = planes.map((plane, index) => ({ plane, index })).filter(({ plane }) => plane?.anchorPos);

    const canvasDimsAvailable =
        typeof canvasWidth === "number" && typeof canvasHeight === "number" && canvasWidth > 0 && canvasHeight > 0;
    const points = canvasDimsAvailable
        ? sectionPlanes.map(({ plane }) => getGizmoScreenPos(view, plane.anchorPos!, canvasWidth!, canvasHeight!))
        : view.convert.worldSpaceToScreenSpace(sectionPlanes.map(({ plane }) => plane.anchorPos!));

    return sectionPlanes.map(({ plane, index }, i) => ({
        plane,
        index,
        point: points[i],
    }));
}

/** Returns the rotation angle to align the double-arrow with the plane's movement direction on screen. */
export function getSectionPlaneArrowAngle(view: View, plane: SectionPlane): number {
    if (!plane?.anchorPos) {
        return 0;
    }

    const normal = vec3.fromValues(plane.normalOffset[0], plane.normalOffset[1], plane.normalOffset[2]);
    const cameraDir = getCameraDir(view.renderState.camera.rotation);
    const isFacingNormal = Math.abs(vec3.dot(normal, cameraDir)) > 0.999;
    if (isFacingNormal) {
        return 0;
    }

    const anchorScreen = view.convert.worldSpaceToScreenSpace([plane.anchorPos], { round: false })[0];
    const offsetPos = vec3.scaleAndAdd(vec3.create(), plane.anchorPos, normal, 1);
    const offsetScreen = view.convert.worldSpaceToScreenSpace([offsetPos], { round: false })[0];

    if (!anchorScreen || !offsetScreen) {
        return 0;
    }

    const dir2d = vec2.sub(vec2.create(), offsetScreen, anchorScreen);
    vec2.normalize(dir2d, dir2d);

    return (Math.atan2(-dir2d[0], dir2d[1]) * 180) / Math.PI;
}

export function getActiveSectionPlaneIndex(planes: SectionPlane[]): number {
    const outlinedIndex = planes.findIndex((plane) => plane.outline.enabled);
    return outlinedIndex === -1 ? planes.length - 1 : outlinedIndex;
}

/**
 * Recenter target for a plane's handle on mode (re)entry.
 */
export function getSectionPlaneViewCenterAnchor(plane: SectionPlane, view: View): vec3 | null {
    if (!plane.anchorPos) {
        return null;
    }

    const { position, rotation } = view.renderState.camera;
    const cameraDir = getCameraDir(rotation as ReadonlyQuat);
    const normal = vec3.fromValues(plane.normalOffset[0], plane.normalOffset[1], plane.normalOffset[2]);

    const denom = vec3.dot(normal, cameraDir);
    if (Math.abs(denom) <= 1e-6) {
        return null;
    }

    const t = (plane.normalOffset[3] - vec3.dot(normal, position)) / denom;
    if (t <= 0) {
        return null;
    }

    return vec3.scaleAndAdd(vec3.create(), position, cameraDir, t);
}

/**
 * Computes the target 3D world position for the section-plane gizmo.
 */
export function computeSectionPlaneGizmoTarget(
    activePlane: SectionPlane,
    view: View,
    canvasWidth: number,
    canvasHeight: number,
    safeArea: ViewportSafeArea,
): vec3 | null {
    if (!activePlane.anchorPos || canvasWidth <= 0 || canvasHeight <= 0) {
        return null;
    }

    const { position: cameraPosition, rotation: cameraRotation } = view.renderState.camera;
    const cameraDir = getCameraDir(cameraRotation as ReadonlyQuat);
    const normal = vec3.fromValues(
        activePlane.normalOffset[0],
        activePlane.normalOffset[1],
        activePlane.normalOffset[2],
    );

    const screenPt = getGizmoScreenPos(view, activePlane.anchorPos, canvasWidth, canvasHeight);

    // ── On-screen guard ─────────────────────────────────────────────────
    const [cx, cy] = clampToSafeArea(screenPt[0], screenPt[1], canvasWidth, canvasHeight, safeArea);
    if (Math.abs(cx - screenPt[0]) < 0.5 && Math.abs(cy - screenPt[1]) < 0.5) {
        return null;
    }

    // ── Build the target ray ────────────────────────────────────────────
    let rayDir: vec3;
    if (view.renderState.camera.kind === "pinhole") {
        rayDir = vec3.create();
        buildPerspectiveRayDir(cameraRotation as ReadonlyQuat, cameraDir, view.renderState.camera.fov, {
            left: 0,
            top: 0,
            width: canvasWidth,
            height: canvasHeight,
        })(cx, cy, rayDir);
    } else {
        rayDir = vec3.clone(cameraDir);
    }

    // ── Ray -> section-plane intersection ─────────────────────────────────
    const denom = vec3.dot(normal, rayDir);
    if (Math.abs(denom) <= 1e-6) {
        return null;
    }

    const t = (activePlane.normalOffset[3] - vec3.dot(normal, cameraPosition)) / denom;
    if (t <= 0) {
        return null;
    }

    return vec3.scaleAndAdd(vec3.create(), cameraPosition, rayDir, t);
}

export function distributeAlongAxis(
    positions: number[],
    spacing: number,
    spanStart: number,
    spanEnd: number,
): number[] {
    const n = positions.length;
    if (n === 0) {
        return [];
    }
    if (n === 1) {
        return [Math.max(spanStart, Math.min(spanEnd, positions[0]))];
    }

    const span = spanEnd - spanStart;

    if (spacing * (n - 1) >= span) {
        const step = span / (n - 1);
        return positions.map((_, i) => spanStart + i * step);
    }

    const result = positions.slice();
    result[0] = Math.max(spanStart, result[0]);
    for (let i = 1; i < n; i++) {
        result[i] = Math.max(result[i], result[i - 1] + spacing);
    }

    const overflow = result[n - 1] - spanEnd;
    if (overflow > 0) {
        for (let i = 0; i < n; i++) {
            result[i] -= overflow;
        }
    }

    return result;
}

export function buildPerspectiveRayDir(
    cameraRotation: ReadonlyQuat,
    cameraDir: ReadonlyVec3,
    fovDeg: number,
    canvasRect: CanvasRect,
): (clientX: number, clientY: number, out: vec3) => void {
    const tanHalfFov = Math.tan((fovDeg * Math.PI) / 360);
    const aspect = canvasRect.width / canvasRect.height;
    const cameraRight = vec3.transformQuat(vec3.create(), vec3.fromValues(1, 0, 0), cameraRotation);
    const cameraUp = vec3.transformQuat(vec3.create(), vec3.fromValues(0, 1, 0), cameraRotation);

    return (clientX: number, clientY: number, out: vec3) => {
        const ndcX = (2 * (clientX - canvasRect.left)) / canvasRect.width - 1;
        const ndcY = -((2 * (clientY - canvasRect.top)) / canvasRect.height - 1);
        vec3.copy(out, cameraDir);
        vec3.scaleAndAdd(out, out, cameraRight, ndcX * aspect * tanHalfFov);
        vec3.scaleAndAdd(out, out, cameraUp, ndcY * tanHalfFov);
        vec3.normalize(out, out);
    };
}
